import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { Event } from '@vibesites/runtime';
import { useContentToken, useVibesiteEvents } from '@vibesites/runtime/react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { ArrowRight, Check, ChevronDown, ExternalLink, RotateCcw } from 'lucide-react';

export type SlotData = Record<string, string | null | undefined>;
type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const INVESTOR_TYPES = [
  'Public Pension',
  'Private Pension',
  'Multi-Family Office',
  'Single-Family Office',
  'High-Net-Worth Individual',
  'Other',
] as const;

const inputClass =
  'w-full rounded-xl border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] px-4 py-3 font-body text-base text-[rgb(var(--ala-text))] outline-none transition placeholder:text-[rgb(var(--ala-text-muted))]/70 focus:border-[rgb(var(--ala-secondary))] focus:ring-2 focus:ring-[rgb(var(--ala-secondary))]/20';
const labelClass = 'mb-2 block font-heading text-sm font-semibold text-[rgb(var(--ala-text))]';

export function trackSpotlight(e: MouseEvent<HTMLElement>) {
  const rect = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--spot-x', `${e.clientX - rect.left}px`);
  e.currentTarget.style.setProperty('--spot-y', `${e.clientY - rect.top}px`);
}

export function Spotlight({ strength = 0.18 }: { strength?: number }) {
  return (
    <span
      className="pointer-events-none absolute inset-0 z-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
      style={{
        background: `radial-gradient(340px circle at var(--spot-x, 50%) var(--spot-y, 50%), rgba(84,158,255,${strength}), transparent 65%)`,
      }}
      aria-hidden
    />
  );
}

export function PortalButton({
  data,
  variant = 'solid',
}: {
  data: SlotData;
  variant?: 'solid' | 'outline-light';
}) {
  const styles =
    variant === 'solid'
      ? 'bg-[rgb(var(--ala-secondary))] text-[rgb(var(--ala-on-secondary))] hover:shadow-[0_10px_30px_rgba(84,158,255,0.45)]'
      : 'border border-[rgb(var(--ala-on-primary))]/40 text-[rgb(var(--ala-on-primary))] hover:border-[rgb(var(--ala-secondary))] hover:bg-[rgb(var(--ala-secondary))]/10';
  return (
    <a
      href={data.url || 'https://www.alapts.com/investors'}
      target={data._target || '_blank'}
      rel="noopener noreferrer"
      className={`group inline-flex items-center gap-2 whitespace-nowrap rounded-full px-7 py-4 font-heading text-sm font-semibold uppercase tracking-wide transition-all duration-300 hover:-translate-y-0.5 ${styles}`}
    >
      {data.label || 'Investor Portal'}
      <ExternalLink className="h-4 w-4 transition-transform group-hover:rotate-12" aria-hidden />
    </a>
  );
}

function parseMetric(value: string | null | undefined) {
  const match = (value || '').match(/^(.*?)(-?[\d,]*\.?\d+)(.*)$/);
  if (!match) return null;
  const digits = match[2];
  return {
    prefix: match[1],
    num: Number.parseFloat(digits.replace(/,/g, '')),
    suffix: match[3],
    commas: digits.includes(','),
    decimals: (digits.split('.')[1] || '').length,
  };
}

function lerpMetric(before: string | null | undefined, after: string | null | undefined, t: number): string {
  const a = parseMetric(before);
  const b = parseMetric(after);
  if (!a || !b) return (t < 0.5 ? before : after) || '';
  const n = a.num + (b.num - a.num) * t;
  const formatted = b.commas
    ? n.toLocaleString('en-US', { minimumFractionDigits: b.decimals, maximumFractionDigits: b.decimals })
    : n.toFixed(b.decimals);
  return `${b.prefix}${formatted}${b.suffix}`;
}

function clampPct(value: string | null | undefined): number {
  const n = Number.parseFloat(value || '');
  return Number.isFinite(n) ? Math.min(Math.max(n, 0), 100) : 0;
}

export function ValueMetric({ data, t }: { data: SlotData; t: number }) {
  const beforePct = clampPct(data.beforeWidth);
  const afterPct = clampPct(data.afterWidth);
  const width = beforePct + (afterPct - beforePct) * t;
  return (
    <div className="flex flex-col gap-3 border-t border-[rgb(var(--ala-on-primary))]/10 pt-5 first:border-t-0 first:pt-0">
      <div className="flex items-end justify-between gap-4">
        <div>
          <span className="block font-heading text-sm text-[rgb(var(--ala-on-primary))]/60">{data.label}</span>
          <span className="mt-1 block font-heading text-[clamp(26px,2.6vw,36px)] font-semibold leading-none tabular-nums text-[rgb(var(--ala-on-primary))]">
            {lerpMetric(data.before, data.after, t)}
          </span>
        </div>
        {data.change ? (
          <span
            className="rounded-full bg-[rgb(var(--ala-secondary))]/15 px-3 py-1 font-heading text-sm font-semibold text-[rgb(var(--ala-secondary))] transition-all duration-300"
            style={{ opacity: t > 0.95 ? 1 : 0.15, transform: `scale(${t > 0.95 ? 1 : 0.9})` }}
          >
            {data.change}
          </span>
        ) : null}
      </div>
      <div className="relative h-2.5 rounded-full bg-[rgb(var(--ala-on-primary))]/[0.06]">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[rgb(var(--ala-secondary))]/40 to-[rgb(var(--ala-secondary))]"
          style={{ width: `${width}%` }}
        />
        <span
          className="absolute -top-1 h-[18px] w-[2px] rounded bg-[rgb(var(--ala-on-primary))]/50"
          style={{ left: `${beforePct}%` }}
          aria-hidden
        />
      </div>
    </div>
  );
}

export function ValueAddSimulator({
  stages,
  wrapStages,
  startLabel,
  endLabel,
  children,
}: {
  stages: string[];
  wrapStages: (node: ReactNode) => ReactNode;
  startLabel: ReactNode;
  endLabel: ReactNode;
  children: (t: number) => ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-20% 0px' });
  const reduceMotion = useReducedMotion();
  const [progress, setProgress] = useState(0);
  const frame = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
  }, []);

  const play = useCallback(() => {
    stop();
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - start) / 2600, 1);
      setProgress((1 - Math.pow(1 - t, 3)) * 100);
      if (t < 1) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
  }, [stop]);

  useEffect(() => {
    if (!inView) return;
    if (reduceMotion) setProgress(100);
    else play();
  }, [inView, reduceMotion, play]);

  useEffect(() => stop, [stop]);

  const t = progress / 100;
  const activeStage = stages.length > 1 ? Math.round(t * (stages.length - 1)) : 0;

  return (
    <div ref={ref} className="flex flex-col gap-7">
      <div className="flex flex-col gap-5">{children(t)}</div>

      <div className="rounded-2xl bg-[rgb(var(--ala-on-primary))]/[0.04] p-4 sm:p-5">
        <div className="mb-3 flex items-center justify-between gap-2 font-heading text-[10px] font-semibold uppercase tracking-[0.08em] text-[rgb(var(--ala-on-primary))]/60 sm:text-xs sm:tracking-[0.12em]">
          <span className="min-w-0">{startLabel}</span>
          <button
            type="button"
            onClick={play}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1 text-[rgb(var(--ala-secondary))] transition-colors hover:bg-[rgb(var(--ala-secondary))]/10"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            Replay
          </button>
          <span className="min-w-0 text-right">{endLabel}</span>
        </div>
        <div className="relative flex h-8 items-center">
          <div className="absolute inset-x-0 h-1.5 rounded-full bg-[rgb(var(--ala-on-primary))]/10" />
          <div className="absolute left-0 h-1.5 rounded-full bg-[rgb(var(--ala-secondary))]" style={{ width: `${progress}%` }} />
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={Math.round(progress)}
            onChange={(e) => {
              stop();
              setProgress(Number(e.target.value));
            }}
            aria-label="Value-add business plan progress"
            className="relative z-10 h-8 w-full cursor-grab appearance-none bg-transparent active:cursor-grabbing [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-white [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_0_0_6px_rgba(84,158,255,0.35)] [&::-webkit-slider-thumb]:transition-transform hover:[&::-webkit-slider-thumb]:scale-110"
          />
        </div>
        {wrapStages(
          <div className="mt-3 flex justify-between gap-1 sm:gap-2">
            {stages.map((stage, i) => (
              <button
                key={`${stage}-${i}`}
                type="button"
                onClick={() => {
                  stop();
                  setProgress(stages.length > 1 ? (i / (stages.length - 1)) * 100 : 100);
                }}
                className={`min-w-0 font-heading text-[10px] font-semibold uppercase tracking-normal transition-colors sm:text-xs sm:tracking-wide md:text-sm ${
                  i === activeStage
                    ? 'text-[rgb(var(--ala-secondary))]'
                    : 'text-[rgb(var(--ala-on-primary))]/40 hover:text-[rgb(var(--ala-on-primary))]/80'
                }`}
              >
                {stage}
              </button>
            ))}
          </div>,
        )}
      </div>
    </div>
  );
}

function buildLeadPayload(
  name: string,
  email: string,
  phone: string,
  details: string[],
  message: string,
): Record<string, unknown> {
  const nameParts = name.trim().split(/\s+/);
  const firstName = nameParts[0] || '';
  return {
    first_name: firstName,
    last_name: nameParts.slice(1).join(' ') || firstName,
    email: email.trim(),
    phone_no: phone.trim(),
    comments: [...details, '', message.trim()].join('\n'),
    message: message.trim(),
  };
}

export function InvestorContactForm({
  formEmailPath,
  sectionId,
  formId = 'investor-contact-form',
}: {
  formEmailPath: string;
  sectionId: string;
  formId?: string;
}) {
  const { emit } = useVibesiteEvents();
  const formEmail = useContentToken(formEmailPath);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [organization, setOrganization] = useState('');
  const [investorType, setInvestorType] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === 'submitting') return;
    if (!name.trim() || !email.trim()) {
      setStatus('error');
      return;
    }
    setStatus('submitting');

    const details = [
      `Inquiry: Capital Markets / Investor Relations`,
      investorType ? `Investor type: ${investorType}` : '',
      organization.trim() ? `Organization: ${organization.trim()}` : '',
    ].filter(Boolean);
    const payload = buildLeadPayload(name, email, phone, details, message);

    try {
      const leads = (
        window as unknown as {
          __mh_leads__?: {
            submitAsync?: (data: Record<string, unknown>, opts?: object) => Promise<{ success?: boolean }>;
          };
        }
      ).__mh_leads__;

      if (leads?.submitAsync) {
        const response = await leads.submitAsync(payload, {});
        if (response?.success !== true) throw new Error('Lead submission failed');
      } else if (formEmail) {
        const subject = encodeURIComponent(`Investor inquiry — ${name.trim()}`);
        const body = encodeURIComponent(
          [`Name: ${name.trim()}`, `Email: ${email.trim()}`, `Phone: ${phone.trim()}`, ...details, '', message.trim()].join(
            '\n',
          ),
        );
        window.location.href = `mailto:${formEmail}?subject=${subject}&body=${body}`;
      } else {
        throw new Error('Form is not configured');
      }

      try {
        emit(Event.contact_form_submitted, {
          sectionId,
          formId,
          formFields: { investorType },
        });
      } catch {
        // Tracking must never break form UX.
      }

      setStatus('success');
      setName('');
      setEmail('');
      setPhone('');
      setOrganization('');
      setInvestorType('');
      setMessage('');
    } catch {
      setStatus('error');
    }
  }

  if (status === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center rounded-2xl bg-[rgb(var(--ala-surface))] px-6 py-14 text-center"
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[rgb(var(--ala-secondary))] text-[rgb(var(--ala-on-secondary))]">
          <Check className="h-7 w-7" aria-hidden />
        </span>
        <p className="mt-5 font-heading text-xl font-semibold text-[rgb(var(--ala-text))]">Thank you.</p>
        <p className="mt-2 font-body text-[rgb(var(--ala-text-muted))]">
          A member of our capital markets team will be in touch shortly.
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-6 font-heading text-sm font-semibold uppercase tracking-wider text-[rgb(var(--ala-secondary))] transition hover:text-[rgb(var(--ala-primary))]"
        >
          Send another message
        </button>
      </motion.div>
    );
  }

  return (
    <form className="space-y-6" onSubmit={handleSubmit} noValidate>
      <div>
        <label htmlFor={`${formId}-investor-type`} className={labelClass}>
          Investor type
        </label>
        <div className="relative">
          <select
            id={`${formId}-investor-type`}
            value={investorType}
            onChange={(e) => setInvestorType(e.target.value)}
            disabled={status === 'submitting'}
            className={`${inputClass} appearance-none pr-10`}
          >
            <option value="">Select an investor type</option>
            {INVESTOR_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[rgb(var(--ala-text-muted))]"
            aria-hidden
          />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor={`${formId}-name`} className={labelClass}>
            Full Name
          </label>
          <input
            id={`${formId}-name`}
            type="text"
            required
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={status === 'submitting'}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={`${formId}-org`} className={labelClass}>
            Organization
          </label>
          <input
            id={`${formId}-org`}
            type="text"
            autoComplete="organization"
            value={organization}
            onChange={(e) => setOrganization(e.target.value)}
            disabled={status === 'submitting'}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={`${formId}-email`} className={labelClass}>
            Email Address
          </label>
          <input
            id={`${formId}-email`}
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === 'submitting'}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={`${formId}-phone`} className={labelClass}>
            Phone Number
          </label>
          <input
            id={`${formId}-phone`}
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={status === 'submitting'}
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label htmlFor={`${formId}-message`} className={labelClass}>
          How can we help?
        </label>
        <textarea
          id={`${formId}-message`}
          rows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={status === 'submitting'}
          className={`${inputClass} min-h-[120px] resize-y`}
        />
      </div>

      {status === 'error' ? (
        <p className="font-body text-sm text-[rgb(var(--ala-accent))]" role="alert">
          Please include your name and email. If the problem continues, email us directly.
        </p>
      ) : null}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="group flex w-full items-center justify-center gap-2 rounded-full bg-[rgb(var(--ala-secondary))] px-6 py-4 font-heading text-sm font-bold uppercase tracking-[0.12em] text-[rgb(var(--ala-on-secondary))] transition-all duration-300 hover:shadow-[0_10px_30px_rgba(84,158,255,0.45)] disabled:cursor-not-allowed disabled:opacity-70"
      >
        {status === 'submitting' ? 'Sending…' : 'Contact Capital Markets'}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
      </button>
    </form>
  );
}
